import { MasterDataSettingListItem, MASTER_DATA_VALIDATION, ProductField } from '../types/master-data-setting-types';

export type FormValueItem = {
  _id?: string;
  value: string;
};

export type FormState = {
  name?: ProductField;
  values: FormValueItem[];
};

export type FormAction =
  | { type: 'SET_VALUE'; payload: { index: number; value: string } }
  | { type: 'ADD_VALUE' }
  | { type: 'REMOVE_VALUE'; payload: number };

export const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case 'SET_VALUE': {
      const newValues = [...state.values];
      newValues[action.payload.index] = { ...newValues[action.payload.index], value: action.payload.value };
      return { ...state, values: newValues };
    }
    case 'ADD_VALUE':
      return { ...state, values: [...state.values, { value: '' }] };
    case 'REMOVE_VALUE':
      return { ...state, values: state.values.filter((_, i) => i !== action.payload) };
    default:
      return state;
  }
};

export const initFormState = (masterDataItem?: MasterDataSettingListItem): FormState => {
  if (masterDataItem) {
    return {
      name: masterDataItem.masterDataName,
      values: masterDataItem.values.map((v) => ({ _id: v._id, value: v.value })),
    };
  }
  return { name: undefined, values: [{ value: '' }] };
};

export const getDuplicateIndexes = (
  values: FormValueItem[],
  isCreateMode: boolean,
  existingValues: string[],
): number[] => {
  const duplicates: number[] = [];
  values.forEach((item, index) => {
    const trimmed = item.value.trim();
    if (!trimmed) return;
    if (isCreateMode && existingValues.includes(trimmed)) {
      duplicates.push(index);
      return;
    }
    const firstOccurrence = values.findIndex((v) => v.value.trim() === trimmed);
    if (firstOccurrence !== index) {
      duplicates.push(index);
    }
  });
  return duplicates;
};

export const validateValues = (values: FormValueItem[], isCreateMode: boolean, existingValues: string[]): boolean => {
  return (
    values.every((v) => v.value.trim().length > 0) &&
    getDuplicateIndexes(values, isCreateMode, existingValues).length === 0
  );
};

export const sanitizeValueInput = (
  rawValue: string,
  validation?: { type: 'text' | 'number' | 'date'; maxLength?: number },
): string => {
  if (validation?.type !== 'number') {
    return rawValue;
  }

  const digitsOnly = rawValue.replace(/[^0-9]/g, '');
  if (!digitsOnly) {
    return digitsOnly;
  }

  return digitsOnly.replace(/^0+(?=\d)/, '');
};
